// MS-6466: the 60-second session watch. Without it, a superseded CRM tab only learns of the
// takeover when one of its own queries happens to fail; with it, the zombie window is bounded
// at one interval — and at ~zero on tab refocus, exactly when a takeover should surface.
//
// The question goes to /usersession/ping on mhub-user-api — the fleet session check, the
// read-only twin of /usersession/create. It answers the takeover with the same wire shape
// create uses, so isSupersededSession decides here too, and ONLY a definitive superseded
// answer acts: network errors, 5xx and missing sessions are ambient (the create path learned
// that treating them as takeover fabricates a "someone signed in elsewhere" explanation).
import { API, Auth } from 'aws-amplify';
import config from '../config';
import { LOCAL_AUTH_SHIM } from './authShim';
import { handleSessionLost, isSupersededSession } from './session';

export const SESSION_CHECK_INTERVAL_MS = 60000;

// Playwright shortens the interval via addInitScript; a build-time env cannot vary per test.
function intervalMs() {
  // eslint-disable-next-line no-underscore-dangle
  const override = typeof window !== 'undefined' && window.__MHUB_SESSION_CHECK_INTERVAL_MS__;
  return Number(override) > 0 ? Number(override) : SESSION_CHECK_INTERVAL_MS;
}

export async function checkSessionOnce({ post, getIdToken, onSessionLost } = {}) {
  let idToken;
  try {
    idToken = getIdToken
      ? await getIdToken()
      : (await Auth.currentSession()).getIdToken().getJwtToken();
  } catch (e) {
    return; // no session to check is not a takeover
  }
  if (!idToken) {
    return;
  }
  try {
    await (post || ((path, init) => API.post('USER', path, init)))(
      '/usersession/ping',
      { body: { id_token: idToken } },
    );
  } catch (error) {
    if (isSupersededSession(error)) {
      (onSessionLost || (() => handleSessionLost(config.app.secureUrl)))();
    }
    // anything else is ambient — never a verdict
  }
}

// Mounted once for the app's lifetime (pages/App). Ticks on the interval and on tab refocus,
// skips hidden tabs (browsers throttle their timers anyway), and is a no-op under the local
// auth shim — the shim's canned API.post would "pass" every ping without checking anything.
// Returns the stop function.
export function startSessionWatch({
  post, getIdToken, onSessionLost, doc, win,
} = {}) {
  if (LOCAL_AUTH_SHIM) {
    return () => {};
  }
  const d = doc || document;
  const w = win || window;
  // One check at a time: a hung ping must not stack another on the next tick. (No abort signal
  // here — Amplify v3's API.post cannot carry one; the gate alone bounds concurrency at 1.)
  let inFlight = false;
  const tick = () => {
    if (inFlight) {
      return;
    }
    if (d.visibilityState === 'hidden') {
      return;
    }
    inFlight = true;
    checkSessionOnce({ post, getIdToken, onSessionLost }).finally(() => {
      inFlight = false;
    });
  };
  const onVisibility = () => {
    if (d.visibilityState === 'visible') {
      tick();
    }
  };
  const timer = w.setInterval(tick, intervalMs());
  d.addEventListener('visibilitychange', onVisibility, false);
  return function stopSessionWatch() {
    w.clearInterval(timer);
    d.removeEventListener('visibilitychange', onVisibility, false);
  };
}
